import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { todoShareNameFromTitle } from '../../utils/todoShareTitle';

export type TodoNoteBoardPickerItem = {
  id: string;
  title: string;
  is_active?: boolean;
};

type TodoNoteBoardPickerProps = {
  boards: TodoNoteBoardPickerItem[];
  selectedBoardId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (boardId: string) => void;
  onEdit?: (boardId: string) => void;
  onCreate?: () => void;
  canCreate?: boolean;
  maxBoards?: number;
  disabled?: boolean;
  /** 대시보드처럼 overflow가 잘리는 헤더에서는 메뉴를 body로 띄운다. */
  portalMenu?: boolean;
  className?: string;
};

const TodoNoteBoardPicker: React.FC<TodoNoteBoardPickerProps> = ({
  boards,
  selectedBoardId,
  open,
  onOpenChange,
  onSelect,
  onEdit,
  onCreate,
  canCreate = true,
  maxBoards = 3,
  disabled = false,
  portalMenu = false,
  className,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);

  const selected = boards.find((b) => b.id === selectedBoardId) ?? null;
  const selectedLabel = selected
    ? todoShareNameFromTitle(selected.title) || '노트'
    : boards.length
      ? '노트 선택'
      : '노트';

  useLayoutEffect(() => {
    if (!open || !portalMenu || !btnRef.current) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      setMenuPos({ top: r.bottom + 4, left: r.left, minWidth: Math.max(r.width, 168) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, portalMenu]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (rootRef.current?.contains(t)) return;
      const menu = document.getElementById('cp-todo-note-board-picker-menu');
      if (menu?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  const menu = open ? (
    <ul
      id="cp-todo-note-board-picker-menu"
      className={'cp-todo-note-board-picker-menu' + (portalMenu ? ' is-portaled' : '')}
      role="listbox"
      aria-label="TODO 노트 목록"
      style={
        portalMenu && menuPos
          ? { top: menuPos.top, left: menuPos.left, minWidth: menuPos.minWidth }
          : undefined
      }
    >
      {boards.map((b) => {
        const isActive = selectedBoardId === b.id;
        const chipName = todoShareNameFromTitle(b.title) || '노트';
        return (
          <li key={b.id} role="presentation" className="cp-todo-note-board-picker-row">
            <button
              type="button"
              role="option"
              aria-selected={isActive}
              className={`cp-todo-note-board-picker-option${isActive ? ' is-active' : ''}${
                b.is_active === false ? ' is-private' : ''
              }`}
              onClick={() => {
                onSelect(b.id);
                onOpenChange(false);
              }}
            >
              {chipName}
            </button>
            {onEdit ? (
              <button
                type="button"
                className="cp-todo-note-board-picker-edit"
                aria-label={`${chipName} 설정`}
                title="노트 설정"
                data-tutorial-todo={isActive ? 'open-note-settings' : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(b.id);
                  onOpenChange(false);
                }}
              >
                <img src={`${process.env.PUBLIC_URL || ''}/edit.svg`} alt="" className="track-icon" />
              </button>
            ) : null}
          </li>
        );
      })}
      {onCreate ? (
        <li role="presentation" className="cp-todo-note-board-picker-add-row">
          <button
            type="button"
            className="cp-todo-note-board-picker-add"
            aria-label="새 TODO 노트 만들기"
            title={
              canCreate ? '새 TODO 노트' : `노트는 최대 ${maxBoards}개까지 만들 수 있습니다`
            }
            disabled={!canCreate}
            onClick={() => {
              if (!canCreate) return;
              onCreate();
              onOpenChange(false);
            }}
          >
            + 새 노트
          </button>
        </li>
      ) : null}
    </ul>
  ) : null;

  return (
    <div
      className={'cp-todo-note-board-picker' + (className ? ` ${className}` : '')}
      ref={rootRef}
    >
      <button
        ref={btnRef}
        type="button"
        className={`cp-todo-note-board-picker-btn${open ? ' is-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`TODO 노트: ${selectedLabel}`}
        disabled={disabled || boards.length === 0}
        onClick={() => onOpenChange(!open)}
      >
        <span className="cp-todo-note-board-picker-label">{selectedLabel}</span>
        <span className="cp-todo-note-board-picker-chevron" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {portalMenu && menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
    </div>
  );
};

export default TodoNoteBoardPicker;
